import {
  createContext,
  ReactNode,
  use,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useRouter } from "next/router";
import { UrlObject } from "url";
import { ParsedUrlQuery } from "node:querystring";
import { routesDefinitions } from "lib/actions/routeDefinitions";

const SideNavContext = createContext<ReturnType<typeof useProvider>>(null);

export const useSideNav = () => use(SideNavContext);

export const SideNavProvider = (p: { children: ReactNode }) => {
  const provider = useProvider();
  return <SideNavContext value={provider}>{p.children}</SideNavContext>;
};

type RoutesState = { [key in keyof typeof routesDefinitions]?: UrlObject };
const initialRoutesState: RoutesState = {
  layers: null,
  views: null,
  models: null,
  scans: null,
  gis: null,
  imagery: null,
  modelElement: null,
  search: null,
  issues: null,
  members: null,
  project: null,
};

function routesReducer(
  state: RoutesState,
  action: { type: string; router?: ReturnType<typeof useRouter> },
): RoutesState {
  switch (action.type) {
    case "RESET_ROUTES": {
      return initialRoutesState;
    }
    case "REMEMBER_ROUTE": {
      const currentUrl = {
        pathname: action.router.pathname,
        query: action.router.query,
      };
      let stateChanges: RoutesState = null;
      for (const routeName in routesDefinitions) {
        if (routesDefinitions[routeName].regex.test(action.router.asPath)) {
          stateChanges = {
            ...stateChanges,
            [routeName]: currentUrl,
          };
        }
      }
      if (stateChanges == null) return state;
      return {
        ...state,
        ...stateChanges,
      };
    }
  }
}

function useProvider() {
  const router = useRouter();
  const [isExpanded, setExpanded] = useState(false);
  const [rememberedStackRoutes, dispatchRememberedRoutesUpdate] = useReducer(
    routesReducer,
    initialRoutesState,
  );
  const projectId = router.query.projectId;

  const isProjectRoot = routesDefinitions.project.regex.test(router.asPath);

  useEffect(() => {
    if (!router.pathname) return;
    dispatchRememberedRoutesUpdate({ type: "REMEMBER_ROUTE", router });
  }, [router.pathname, router.query]);

  useEffect(() => {
    dispatchRememberedRoutesUpdate({ type: "RESET_ROUTES" });
  }, [projectId]);

  useEffect(() => {
    if (isProjectRoot) setExpanded(true);
  }, [isProjectRoot]);

  return {
    isExpanded,
    collapseWhenUsed: isExpanded && isProjectRoot,
    routes: useMemo(
      () =>
        Object.keys(routesDefinitions)
          .filter((routeName) => !!routesDefinitions[routeName].href)
          .reduce(
            (acc, routeName) => ({
              ...acc,
              [routeName]: mergeUrlWithCurrentProject(
                routesDefinitions[routeName].regex.test(router.asPath)
                  ? routesDefinitions[routeName].href //root of stack if currently in stack
                  : rememberedStackRoutes[routeName] ?? //last remembered page of stack if not currently in stack
                      routesDefinitions[routeName].href,
                router.query,
              ),
            }),
            null as RoutesState,
          ),
      [rememberedStackRoutes, router.asPath, router.query],
    ),
    toggleExpanded: (value?: boolean) => setExpanded((prev) => value ?? !prev),
  };
}

function mergeUrlWithCurrentProject(url: UrlObject, query: ParsedUrlQuery) {
  if (typeof url.query === "string") throw new Error("query must be an object");
  const { projectId } = query;
  return {
    ...url,
    query: { ...url.query, projectId },
  };
}
